import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import TripConfigurationPanel from '../components/dashboard/TripConfigurationPanel';
import MapWorkspace from '../components/dashboard/MapWorkspace';
import LogSheetDrawer from '../components/dashboard/LogSheetDrawer';
import './Dashboard.css';

const Dashboard = ({ currentPage, onNavigate, onSignOut, userProfile, tripPlan, setTripPlan, darkMode }) => {
    const [isLogDrawerOpen, setIsLogDrawerOpen] = useState(false);
    const [currentLogIndex, setCurrentLogIndex] = useState(0);
    const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
    const [isPanelHovered, setIsPanelHovered] = useState(false);
    // 'form' | 'map'
    const [activeMobileTab, setActiveMobileTab] = useState(tripPlan ? 'map' : 'form');

    const hasTrip = tripPlan && tripPlan.log_sheets && tripPlan.log_sheets.length > 0;
    const miles = tripPlan?.total_trip_miles ? Math.round(tripPlan.total_trip_miles) : 0;
    const stopsCount = tripPlan?.stops?.length || 0;

    const handleTripGenerated = (newTrip) => {
        setTripPlan(newTrip);
        setActiveMobileTab('map');
        // Collapse panel after generating trip
        setIsPanelCollapsed(true);
    };

    const handlePanelToggle = () => {
        setIsPanelCollapsed(!isPanelCollapsed);
    };

    return (

        <div className="dashboard-v2">
            {/* Header */}
            <header className="dashboard-header">
                <div className="header-brand">
                    <div className="brand-icon">
                        <span className="material-symbols-outlined">local_shipping</span>
                    </div>
                    <span className="brand-name">SpotLogs</span>
                </div>

                <div className="header-actions">
                    <div className="user-section">
                        <div className="user-info">
                            <span className="user-label">Driver Account</span>
                            <span className="user-name">{userProfile?.driverName || 'Driver'}</span>
                        </div>
                        <div className="user-avatar">
                            <span className="material-symbols-outlined">person</span>
                        </div>
                        <button className="settings-btn" onClick={() => onNavigate('settings')}>
                            <span className="material-symbols-outlined">settings</span>
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="dashboard-main">
                <div className={`map-area ${activeMobileTab === 'form' ? 'mobile-hidden' : ''}`}>
                    <MapWorkspace tripPlan={tripPlan} />

                    {!hasTrip && (
                        <div className="empty-state">
                            <div className="empty-card">
                                <span className="material-symbols-outlined empty-icon">route</span>
                                <h2>Ready to Plan?</h2>
                                <p>Switch to the <strong>Trip</strong> tab to plan your route.</p>
                                <button className="start-btn-mobile" onClick={() => setActiveMobileTab('form')}>Go to Trip Planner</button>
                            </div>
                        </div>
                    )}

                    {tripPlan && (
                        <div className="trip-stats glass">
                            <div className="stat-item">
                                <span className="stat-value">{miles}</span>
                                <span className="stat-label">Total Miles</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-value">{stopsCount}</span>
                                <span className="stat-label">Stops</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-value">{userProfile?.carrierName || '—'}</span>
                                <span className="stat-label">Carrier</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Collapsible Trip Sidebar */}
                <aside 
                    className={`trip-sidebar glass ${activeMobileTab === 'form' ? 'mobile-visible' : ''} ${isPanelCollapsed ? 'collapsed' : ''} ${isPanelHovered ? 'hovered' : ''}`}
                    onMouseEnter={() => isPanelCollapsed && setIsPanelHovered(true)}
                    onMouseLeave={() => setIsPanelHovered(false)}
                >
                    {/* Toggle button when collapsed */}
                    {isPanelCollapsed && (
                        <button 
                            className="panel-toggle-btn"
                            onClick={handlePanelToggle}
                            onMouseEnter={() => setIsPanelHovered(true)}
                        >
                            <span className="material-symbols-outlined">
                                {isPanelHovered ? 'chevron_right' : 'edit_note'}
                            </span>
                        </button>
                    )}
                    
                    <TripConfigurationPanel
                        setTripPlan={handleTripGenerated}
                        userProfile={userProfile}
                        darkMode={darkMode}
                        isCollapsed={isPanelCollapsed}
                        onToggleCollapse={handlePanelToggle}
                    />
                    
                    {/* Close button when expanded and has trip */}
                    {!isPanelCollapsed && hasTrip && (
                        <button 
                            className="panel-close-btn"
                            onClick={() => setIsPanelCollapsed(true)}
                            title="Minimize panel"
                        >
                            <span className="material-symbols-outlined">chevron_left</span>
                        </button>
                    )}
                </aside>
            </main>

            <div className="mobile-bottom-bar glass">
                 <button 
                    className={`nav-item ${activeMobileTab === 'form' ? 'active' : ''}`}
                    onClick={() => setActiveMobileTab('form')}
                >
                    <span className="material-symbols-outlined">edit_note</span>
                    <span>Trip</span>
                </button>
                <button 
                    className={`nav-item ${activeMobileTab === 'map' ? 'active' : ''}`}
                    onClick={() => setActiveMobileTab('map')}
                >
                    <span className="material-symbols-outlined">map</span>
                    <span>Map</span>
                </button>
                <button 
                    className="nav-item"
                    onClick={() => setIsLogDrawerOpen(true)}
                    disabled={!hasTrip}
                    style={{ opacity: hasTrip ? 1 : 0.5 }}
                >
                    <span className="material-symbols-outlined">description</span>
                    <span>Logs</span>
                </button>
            </div>

            <footer className="dashboard-footer">
                <div className="footer-left">
                    <span className="eld-status"><span className="dot green"></span> ELD Connection Active</span>
                    <span className="sync-status"><span className="material-symbols-outlined">history</span> Last sync: 2 mins ago</span>
                </div>
                <div className="footer-right">
                    <span>© 2024 SpotLogs Inc.</span>
                    <button onClick={() => onNavigate('help')}>Help & Support</button>
                </div>
            </footer>

            <AnimatePresence>
                {hasTrip && (
                    <LogSheetDrawer
                        logSheets={tripPlan.log_sheets}
                        tripMeta={tripPlan.meta || {}}
                        pickupLocation={tripPlan.pickup_location_str}
                        dropoffLocation={tripPlan.dropoff_location_str}
                        currentLogIndex={currentLogIndex}
                        setCurrentLogIndex={setCurrentLogIndex}
                        userProfile={userProfile}
                        isDrawerOpen={isLogDrawerOpen}
                        setIsDrawerOpen={setIsLogDrawerOpen}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default Dashboard;
